<template>
  <Page
    v-if="!anyError"
    relative
    :title="{
      name: 'Discover',
      backLocation: href.discover.index,
    }"
  >
    <div>
      <div class="flex justify-center items-center gap-2 mb-2">
        <TagIcon :tag="tag" class="w-8 h-8" />
        <h2 class="text-2xl font-medium">
          {{ t(`components.tag.${tag}.name`) }}
        </h2>
      </div>
      <p class="sm:text-sm text-xs text-center max-w-2xl md:mx-auto mx-6">
        {{ t(`components.tag.${tag}.description`) }}
      </p>
    </div>
    <!-- Make a component of this later if it's good -->
    <ContextMenu
      close-on-outside-click
      default-position="bottom-left"
      :custom-position="
        sm
          ? {
              top: '7rem',
            }
          : {}
      "
    >
      <template #facing="{ open, close, isOpen }">
        <button
          class="sm:absolute block ml-auto right-0 top-4 py-1 px-2 rounded-md hover:bg-neutral-200 hover:dark:bg-neutral-800"
          @click="() => (isOpen ? close() : open())"
        >
          Ranked by
          {{
            rankingOptions
              .find(({ value }) => value === query.rankBy)
              ?.text.capitalize()
          }}
          <IconChevronDown />
        </button>
      </template>
      <template #menu="{ close }">
        <ul
          class="w-48 p-2 rounded-md bg-neutral-100 dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-800"
        >
          <li v-for="option in rankingOptions">
            <button
              class="w-full flex justify-between items-center gap-2 text-left p-1 hover:bg-neutral-200 hover:dark:bg-neutral-700 rounded-md"
              :class="{
                'text-nami-comi-blue': option.value === query.rankBy,
              }"
              @click="
                () => {
                  query.rankBy = option.value;
                  close();
                }
              "
            >
              <div class="flex items-center gap-2">
                <component
                  :size="20"
                  :is="
                    option.value === 'followCount'
                      ? IconBookmark
                      : option.value === 'views'
                        ? IconEye
                        : option.value === 'commentCount'
                          ? IconMessageDots
                          : IconStar
                  "
                />
                {{ option.text }}
              </div>
              <IconCheck :size="20" v-if="option.value === query.rankBy" />
            </button>
          </li>
        </ul>
      </template>
    </ContextMenu>
    <h3 class="text-xl font-medium">The Top 3</h3>
    <div v-if="top10Pending" class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      <Skeleton class="h-64 sm:h-72 md:h-96" />
      <Skeleton class="hidden md:block h-64 sm:h-72 md:h-96" />
      <Skeleton class="hidden lg:block h-64 sm:h-72 md:h-96" />
    </div>
    <Error v-else-if="top10Error" :error="top10Error" @retry="top10Refresh" />
    <Swiper
      v-else-if="top10Titles"
      class="rounded-md"
      :autoplay="{ disableOnInteraction: true, delay: 5000 }"
      :modules="[Autoplay]"
      :breakpoints="{
        '1280': { slidesPerView: 3 },
        '1024': { slidesPerView: 2 },
      }"
      :slides-per-view="1"
      :space-between="16"
    >
      <SwiperSlide v-for="(title, index) in parsedTop3" class="relative">
        <TheNuxtLink :to="title.href">
          <AsyncImage
            class="w-full h-64 sm:h-72 md:h-96 object-cover object-top rounded-md"
            :src="title.coverUrl"
            :alt="title.title"
          />
          <div class="absolute left-0 bottom-0 w-full p-1">
            <div class="p-2 bg-neutral-200 dark:bg-neutral-800 rounded-md">
              <p class="truncate font-semibold">
                {{ title.title }}
              </p>
              <TitleStats :title-id="title.id" small />
            </div>
          </div>
          <span
            class="absolute left-2 top-2 w-12 h-12 flex items-center justify-center rounded-full font-bold text-xl bg-neutral-200 dark:bg-neutral-800 border-2 border-nami-comi-blue"
          >
            {{ index + 1 }}
          </span>
        </TheNuxtLink>
      </SwiperSlide>
    </Swiper>
    <h3 id="moreTitlesForTag" class="text-xl font-medium !mb-4 !mt-16">
      More {{ t(`components.tag.${tag}.name`) }} titles
    </h3>
    <ul
      v-if="restTopPending"
      class="grid gap-x-4 gap-y-16 grid-cols-3 md:grid-cols-4 xl:grid-cols-6"
    >
      <li v-for="_ in expectedTitlesInPage">
        <Skeleton class="w-full aspect-[5/7]" />
        <Skeleton class="w-full h-12 mt-2" />
      </li>
    </ul>
    <Error
      v-else-if="restTopError"
      :error="restTopError"
      @retry="restTopRefresh"
    />
    <ul
      v-else-if="restTopTitles"
      class="grid gap-x-4 gap-y-16 grid-cols-3 md:grid-cols-4 xl:grid-cols-6"
    >
      <li v-for="(title, index) in parsedRestTopTitles" class="relative">
        <TheNuxtLink :to="title.href">
          <AsyncImage
            class="w-full aspect-[5/7] object-cover rounded-md transition hover:brightness-90"
            :src="title.coverUrl"
            :alt="title.title"
          />
          <span class="line-clamp-2 mt-1">
            {{ title.title }}
          </span>
        </TheNuxtLink>
        <span
          class="absolute pointer-events-none left-2 top-2 flex items-center justify-center rounded-full font-medium w-10 h-10 bg-neutral-200 dark:bg-neutral-800 border-2 border-nami-comi-blue"
          :class="index + 1 + 3 + offset < 100 ? 'text-lg' : 'text-sm'"
        >
          {{ index + 1 + 3 + offset }}
        </span>
      </li>
    </ul>
    <Pagination
      v-if="restTopTitles"
      class="my-4"
      v-model="page"
      :meta="restTopTitles.meta"
      hide-total
    />
    <h3 class="text-xl font-medium !mb-4 !mt-16">Recently Published</h3>
    <NamiLoading v-if="recentPending" />
    <Error
      v-else-if="recentError"
      :error="recentError"
      @retry="recentRefresh"
    />
    <TitlesCarouselWithHoverExpand
      v-else-if="recentTitles"
      navigation-id="recents"
      wrapper-class="-mx-4 px-4 lg:-mx-8 lg:px-8 overflow-hidden"
      :titles="recentTitles.data"
      :breakpoints="{
        0: { slidesPerView: 2, spaceBetween: 8, autoHeight: true },
        360: { slidesPerView: 3, spaceBetween: 8, autoHeight: true },
        640: { slidesPerView: 4, spaceBetween: 8, autoHeight: true },
        768: { slidesPerView: 5, spaceBetween: 8, autoHeight: true },
        1024: { slidesPerView: 6, spaceBetween: 16, autoHeight: true },
        1280: { slidesPerView: 7, spaceBetween: 16, autoHeight: true },
        1536: { slidesPerView: 8, spaceBetween: 16, autoHeight: true },
      }"
      class="!mb-16"
    />
  </Page>
  <ErrorPage v-else :error="anyError" />
</template>

<script setup lang="ts">
import {
  Title,
  getRelationship,
  type TitleFuzzyListOrderOptions,
} from "~/src/api";
import { parseTitleForDisplay } from "~/utils/display/title";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import {
  IconChevronDown,
  IconBookmark,
  IconEye,
  IconMessageDots,
  IconStar,
  IconCheck,
} from "@tabler/icons-vue";
import { capitalize } from "vue";

const nuxtApp = useNuxtApp();
const route = useRoute();
const { t, locale } = nuxtApp.$i18n.global;
const tag = route.params.tag as string;

const rankingOptions: { text: string; value: TitleFuzzyListOrderOptions }[] = [
  { text: "Views", value: "views" },
  { text: "Follows", value: "followCount" },
  { text: "Comments", value: "commentCount" },
  { text: "Rating", value: "rating" },
];

const urlForm = {
  rankBy: { type: "string", default: "views" },
} satisfies QueryTemplate;

const { query } = useUrlForm<
  typeof urlForm,
  {
    rankBy: TitleFuzzyListOrderOptions;
  }
>(urlForm);

const { page, limit, offset } = usePagination(1, 24);
const { sm } = useBreakpoints();
const href = useHref();

const {
  data: top10Titles,
  pending: top10Pending,
  error: top10Error,
  refresh: top10Refresh,
} = useAsyncData(
  `top-10-titles-tag-${tag}`,
  async () => {
    const titles = await Title.fuzzySearch({
      primaryTag: tag,
      limit: 3,
      order: { [query.rankBy]: "desc" },
      contentRatings: ["safe", "mature", "restricted"],
      includes: ["cover_art"],
    });

    return {
      titles: titles.data,
    };
  },
  { server: false, watch: [() => route.query] },
);

const parsedTop3 = computed(
  () =>
    top10Titles.value?.titles.map((title) => parseTitleForDisplay(title)) ?? [],
);

const {
  data: restTopTitles,
  pending: restTopPending,
  error: restTopError,
  refresh: restTopRefresh,
} = useAsyncData(
  `rest-top-titles-tag-${tag}`,
  async () => {
    const titles = await Title.fuzzySearch({
      primaryTag: tag,
      limit: limit.value,
      offset: offset.value + 3,
      contentRatings: ["safe", "mature", "restricted"],
      order: { [query.rankBy]: "desc" },
      includes: ["cover_art"],
    });

    return titles;
  },
  { watch: [limit, offset, () => route.query] },
);

const anyError = computed(() => top10Error.value || restTopError.value);
use404Response(anyError);

const expectedTitlesInPage = computed(() => {
  if (!restTopTitles.value) return limit.value;

  const { total } = restTopTitles.value.meta;
  const partition = Array.partition(total - 3, limit.value);

  return partition[page.value - 1];
});

watch(page, () => {
  setTimeout(() => {
    scrollToElement("#moreTitlesForTag", {
      behavior: "smooth",
      block: "center",
    });
  }, 100);
});

const parsedRestTopTitles = computed(() => {
  return restTopTitles.value?.data.map((title) =>
    parseTitleForDisplay(title, "med"),
  );
});

const {
  data: recentTitles,
  pending: recentPending,
  error: recentError,
  refresh: recentRefresh,
} = useAsyncData(`recently-added-titles-tag-${tag}`, async () => {
  return await Title.fuzzySearch({
    primaryTag: tag,
    limit: 10,
    contentRatings: ["safe", "mature", "restricted"],
    order: { publishedAt: "desc" },
    includes: ["cover_art"],
  });
});

const seo = {
  title: `${capitalize(tag)} (Genre)`,
  description: `Browse the ${capitalize(tag)} titles on NamiComi.`,
  //slug: data.attributes.slug,
  coverUrl: "",
  href: `/en/discover/${tag}`,
};

nuxtApp.runWithContext(() =>
  useSeoMeta({
    title: seo.title,
    ogTitle: seo.title,
    twitterTitle: seo.title,
    description: seo.description,
    ogDescription: seo.description,
    twitterDescription: seo.description,
    ogUrl: seo.href,
    ogLocale: locale.value,
    ogImage: seo.coverUrl,
    ogImageSecureUrl: seo.coverUrl,
    twitterImage: seo.coverUrl,
    //ogImageHeight: "1200",
    //ogImageWidth: "630",
    ogImageUrl: seo.coverUrl,
    //ogImageType: "image/png",
    ogImageAlt: "NamiComi",
    ogLocaleAlternate: "en_GB",
  }),
);
</script>
